import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Smartphone, ShieldCheck } from "lucide-react";
import { scrollToId } from "./Reveal";
import { useLang } from "../../i18n";

const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ parts, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.12, ease }}
    >
      {parts[0]}
      {parts[1] && <span className="text-outline-accent italic">{parts[1]}</span>}
    </motion.span>
  </span>
);

const TiltFrame = ({ t }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1400px]">
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        transition={{ duration: 1.4, delay: 0.5, ease }}
        className="relative aspect-[4/5] w-full overflow-hidden rounded-[28px] shadow-[0_60px_120px_-40px_rgba(0,0,0,0.8)] sm:aspect-[5/4] lg:aspect-[4/5]"
        data-testid="hero-image-frame"
      >
        <motion.img
          src="/images/hero.jpg"
          alt={t.alt}
          style={{ y: imgY, scale: 1.18 }}
          className="absolute inset-0 h-full w-full object-cover"
          fetchpriority="high"
        />
        <div className="spotlight absolute inset-0" />
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-2xl border border-white/15 bg-[#1f1f1f]/60 px-4 py-3 backdrop-blur-xl">
          <span className="flex items-center gap-2 text-xs font-semibold text-white sm:text-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#7BD88F] opacity-70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#7BD88F]" />
            </span>
            {t.door}
          </span>
          <Smartphone className="h-4 w-4 text-[#F7D048]" />
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 0.9, delay: 1.3, ease }}
        className="absolute -left-3 -top-5 grid h-24 w-24 place-items-center rounded-full bg-[#F7D048] text-center text-[#2D2D2D] shadow-xl sm:-left-8 sm:h-28 sm:w-28"
        data-testid="hero-discount-badge"
      >
        <span className="leading-none">
          <span className="font-display block text-3xl font-extrabold sm:text-4xl">-20%</span>
          <span className="text-[10px] font-bold uppercase tracking-widest">{t.discount}</span>
        </span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t: all, lang } = useLang();
  const t = all.hero;
  return (
  <section id="inici" data-testid="hero-section" className="grain relative overflow-hidden bg-[#232323] pb-20 pt-28 text-[#FDFBF7] sm:pt-32 lg:pb-28">
    <div className="pointer-events-none absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-[#F26D5A]/20 blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-16">
      <div>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#F7D048]/40 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#F7D048]"
        >
          <ShieldCheck className="h-3.5 w-3.5" /> {t.badge}
        </motion.p>
        <h1 key={lang} data-testid="hero-title" className="font-display text-[2.5rem] font-extrabold leading-[0.95] tracking-tight sm:text-6xl lg:text-[4.9rem]">
          <span className="mb-4 block overflow-hidden">
            <motion.span
              className="block text-xl font-bold tracking-normal text-[#D9D2C8] sm:text-2xl lg:text-3xl"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.15, ease }}
            >
              {t.pre}
            </motion.span>
          </span>
          {t.lines.map((parts, i) => <MaskLine key={i} parts={parts} i={i} />)}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease }}
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl text-base leading-relaxed text-[#D9D2C8] md:text-lg"
        >
          {t.subtitle}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            onClick={() => scrollToId("reserva")}
            data-testid="hero-cta-btn"
            className="btn-magnet group inline-flex items-center justify-between gap-4 rounded-full bg-[#F26D5A] py-2 pl-6 pr-2 text-left text-sm font-bold text-[#1f1f1f] sm:text-base"
          >
            <span>{t.cta}</span>
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#1f1f1f] text-[#F7D048] transition-transform duration-500 group-hover:rotate-[-45deg]">
              <ArrowDownRight className="h-5 w-5" />
            </span>
          </button>
          <button
            onClick={() => scrollToId("mides")}
            data-testid="hero-sizes-btn"
            className="link-underline self-start whitespace-nowrap text-sm font-semibold text-[#EDE6DC] sm:self-auto"
          >
            {t.sizes}
          </button>
        </motion.div>
      </div>
      <TiltFrame t={t} />
    </div>
  </section>
  );
};
